import React, { useContext } from "react";
import {
  Paper,
  Typography,
  Grid,
  Box,
  FormControl,
  FormGroup,
  FormControlLabel,
  Switch,
  RadioGroup,
  TextField,
  Radio,
} from "@mui/material";
import { EventContext } from "../../../../../contexts/EventContext";

/*
  AutomationSettings

  Controls automated behaviors that occur during or after the event.
  These settings reduce manual work for organizers.

  Components that will live here:

  - AutoCompleteEventToggle
  - AutoSendMatchesToggle
*/

const AutomationSettings = ({ settings, handleChange }) => {
  const { pairingEvent } = useContext(EventContext);
  const forceTimeMode = ["mixer", "dinner"].includes(
    pairingEvent?.pairingEventType,
  );
  const autoCompleteMode = forceTimeMode ? "time" : settings.autoCompleteMode;

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="subtitle1" sx={{ mb: 2 }}>
        Event Automation
      </Typography>

      <Typography variant="caption" sx={{ display: "block", mb: 3 }}>
        Configure automated actions that occur during or after the event.
      </Typography>

      <Grid container spacing={3}>
        {/* Auto Complete Event */}
        <Grid item xs={12}>
          <Box sx={{ width: "100%", mb: 2 }}>
            <FormControl variant="standard">
              <FormGroup>
                <FormControlLabel
                  sx={{ mb: 2 }}
                  control={
                    <Switch
                      checked={settings.autoComplete}
                      onChange={() =>
                        handleChange("autoComplete", !settings.autoComplete)
                      }
                    />
                  }
                  label="Auto Complete Event"
                />
                {settings?.autoComplete ? (
                  <>
                    {forceTimeMode ? (
                      <Typography variant="caption" sx={{ mb: 1 }}>
                        Mixer and Dinner events must be auto completed at a
                        specific time.
                      </Typography>
                    ) : (
                      <FormControl
                        component="fieldset"
                        fullWidth
                        sx={{ mb: 1 }}
                      >
                        <RadioGroup
                          row
                          value={autoCompleteMode}
                          onChange={(e) =>
                            handleChange("autoCompleteMode", e.target.value)
                          }
                        >
                          <FormControlLabel
                            value="minutes"
                            control={<Radio />}
                            label="After minutes"
                          />
                          <FormControlLabel
                            value="time"
                            control={<Radio />}
                            label="At specific time"
                          />
                        </RadioGroup>
                      </FormControl>
                    )}
                    {autoCompleteMode === "minutes" ? (
                      <TextField
                        type="number"
                        inputProps={{
                          min: 1,
                          step: 1,
                          pattern: "[0-9]*",
                          inputMode: "numeric",
                        }}
                        label="Auto Complete After (minutes)"
                        value={settings.autoCompleteAfter}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (/^\d*$/.test(val)) {
                            handleChange(
                              "autoCompleteAfter",
                              val === "" ? "" : parseInt(val, 10),
                            );
                          }
                        }}
                        variant="outlined"
                        size="small"
                        sx={{
                          mb: 1.5,
                          mt: 0.5,
                          maxWidth: 220,
                          "& .MuiInputBase-root": { background: "#f9fafc" },
                        }}
                        fullWidth
                        placeholder="e.g. 120"
                        helperText="The event will be completed this many minutes once the last round has started"
                      />
                    ) : (
                      <TextField
                        type="time"
                        label="Complete At"
                        value={settings.autoCompleteTime}
                        onChange={(e) =>
                          handleChange("autoCompleteTime", e.target.value)
                        }
                        variant="outlined"
                        size="small"
                        sx={{ mb: 1.5, mt: 0.5, maxWidth: 220 }}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        helperText="Select clock time to complete the event"
                      />
                    )}
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.autoSendMatches}
                          onChange={() =>
                            handleChange(
                              "autoSendMatches",
                              !settings.autoSendMatches,
                            )
                          }
                        />
                      }
                      label="Auto Send Matches"
                    />
                    <Typography variant="caption" sx={{ pl: 1 }}>
                      When enabled, all matches will be automatically emailed to
                      attendees after the event is completed.
                    </Typography>
                  </>
                ) : null}
              </FormGroup>
            </FormControl>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default AutomationSettings;
